// src/pages/analytics/SevenVs28Tab.tsx
//
// 7 vs 28 Days — this-week vs trailing-4-week-average comparison (recipe:
// sales-7v28). Two layouts:
//   • Side by side (default, the PBI page): Item Category and Customer
//     Category matrices next to each other.
//   • Single: dimension pills pick one grouping, with the payments columns.
// Every table also carries "7D LY" — the same 7 days last year (shifted 364
// days so weekdays line up) — and its YoY %. Two variance charts sit above:
// by salesman (last 7 vs 4-week avg) and by day of week (each weekday vs the
// average of that weekday over the prior 4 weeks), plus the daily strip.
// 7D LY / DOW variance need agent 3.20.0+; older agents just omit them.
//
// Date strings (dateTo / last7From / prior28From / days[].date) come from
// the agent already formatted — rendered as-is, never through new Date().

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Scale, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell, ReferenceLine } from 'recharts';
import { apiV2 as api } from '../../services/apiV2';
import type { SevenVs28Row, SevenVs28Response } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterPane, AnalyticsSavedViews } from './filterPane';
import { useFilterPane } from './filterPaneState';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));
const fmtK = (v: number) => `${v < 0 ? '-' : ''}$${(Math.abs(v) / 1000).toFixed(0)}k`;

const DIMENSIONS: { value: string; label: string }[] = [
  { value: 'category',      label: 'Customer Category' },
  { value: 'area',          label: 'Area' },
  { value: 'salesman',      label: 'Salesman' },
  { value: 'customer',      label: 'Customer' },
  { value: 'item_category', label: 'Item Category' },
  { value: 'item_subcategory', label: 'Item Subcategory' },
];
const dimLabel = (d: string) => DIMENSIONS.find(x => x.value === d)?.label ?? d;

const MEASURES: { value: string; label: string }[] = [
  { value: 'dollars', label: 'Dollars' },
  { value: 'qty',     label: 'Qty' },
];

type Layout = 'split' | 'single';
const LAYOUTS: { value: Layout; label: string }[] = [
  { value: 'split',  label: 'Side by side' },
  { value: 'single', label: 'Single' },
];

const pill = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;

function exportRows(data: SevenVs28Response, dimension: string, exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<SevenVs28Row> = [
    [dimension,          (r) => r.label],
    ['Last 7 Days',      (r) => r.sales7],
    ['4-Wk Weekly Avg',  (r) => r.prior7Avg],
    ['Delta %',          (r) => r.deltaPct],
    ['7D Last Year',     (r) => r.sales7Ly ?? ''],
    ['7D YoY %',         (r) => r.yoyPct ?? ''],
    ['Payments 7d',      (r) => r.payments7],
    ['Payments 4-Wk Avg', (r) => r.paymentsPrior7Avg],
  ];
  exporter(`sales-7v28-${dimension.toLowerCase().replace(/\s+/g, '-')}`, cols, [...data.rows, { ...data.totals, label: 'TOTAL' }]);
}

function DeltaCell({ pct }: { pct: number | null | undefined }) {
  if (pct == null) return <span className="text-slate-600">–</span>;
  const up = pct >= 0;
  return (
    <span className={`tabular-nums font-medium ${up ? 'text-emerald-400' : 'text-red-400'}`}>
      {up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function CompareTable({ title, data, fmtVal, showPayments }: {
  title: string; data: SevenVs28Response | undefined; fmtVal: (v: number) => string; showPayments: boolean;
}) {
  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const hasLy = totals?.sales7Ly != null;
  const th = 'px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap';
  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden min-w-0">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-700/50 bg-slate-800/50">
              <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[140px]">{title}</th>
              <th className={th}>Last 7</th>
              <th className={th}>Prior 28 Avg</th>
              <th className={th}>Δ%</th>
              {hasLy && <th className={`${th} border-l border-slate-700/40`}>7D LY</th>}
              {hasLy && <th className={th}>7D YoY %</th>}
              {showPayments && <th className={`${th} border-l border-slate-700/40`}>Payments 7d</th>}
              {showPayments && <th className={th}>Payments 4-Wk Avg</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.label}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[220px] truncate" title={r.label}>{r.label}</td>
                <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtVal(r.sales7)}</td>
                <td className="px-3 py-2 text-right text-slate-400 tabular-nums">{fmtVal(r.prior7Avg)}</td>
                <td className="px-3 py-2 text-right"><DeltaCell pct={r.deltaPct} /></td>
                {hasLy && <td className="px-3 py-2 text-right text-slate-400 tabular-nums border-l border-slate-800/60">{fmtVal(r.sales7Ly ?? 0)}</td>}
                {hasLy && <td className="px-3 py-2 text-right"><DeltaCell pct={r.yoyPct} /></td>}
                {showPayments && <td className="px-3 py-2 text-right text-slate-300 tabular-nums border-l border-slate-800/60">{fmtCur(r.payments7)}</td>}
                {showPayments && <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{fmtCur(r.paymentsPrior7Avg)}</td>}
              </tr>
            ))}
          </tbody>
          {totals && rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                <td className="px-3 py-2 text-right text-white tabular-nums">{fmtVal(totals.sales7)}</td>
                <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtVal(totals.prior7Avg)}</td>
                <td className="px-3 py-2 text-right"><DeltaCell pct={totals.deltaPct} /></td>
                {hasLy && <td className="px-3 py-2 text-right text-slate-300 tabular-nums border-l border-slate-700/60">{fmtVal(totals.sales7Ly ?? 0)}</td>}
                {hasLy && <td className="px-3 py-2 text-right"><DeltaCell pct={totals.yoyPct} /></td>}
                {showPayments && <td className="px-3 py-2 text-right text-slate-200 tabular-nums border-l border-slate-700/60">{fmtCur(totals.payments7)}</td>}
                {showPayments && <td className="px-3 py-2 text-right text-slate-400 tabular-nums">{fmtCur(totals.paymentsPrior7Avg)}</td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {rows.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">No rows to show</div>}
    </div>
  );
}

/** Horizontal-ish variance bars: green above the 4-week average, red below. */
function VarianceChart({ title, data, height = 'h-56', fmt }: {
  title: string; data: { label: string; variance: number; detail: string }[]; height?: string; fmt: (v: number) => string;
}) {
  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4 min-w-0">
      <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>
      {data.length === 0 ? (
        <div className="text-center py-6 text-slate-500 text-sm">No data</div>
      ) : (
        <div className={height}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
              <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                interval={0} angle={data.length > 8 ? -30 : 0} textAnchor={data.length > 8 ? 'end' : 'middle'} height={data.length > 8 ? 50 : 24} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} tickFormatter={(v) => fmt(Number(v))} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Tooltip
                cursor={{ fill: '#475569', fillOpacity: 0.15 }}
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                labelStyle={{ color: '#f1f5f9' }}
                formatter={(value, _name, item) => [`${fmt(Number(value))} · ${(item?.payload as { detail?: string })?.detail ?? ''}`, 'vs 4-wk avg']}
              />
              <Bar dataKey="variance" radius={[3, 3, 0, 0]} maxBarSize={36}>
                {data.map((d, i) => <Cell key={i} fill={d.variance >= 0 ? '#10b981' : '#ef4444'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function SevenVs28Tab() {
  const [layout, setLayout] = useState<Layout>('split');
  const [dimension, setDimension] = useState('category');
  const [measure, setMeasure] = useState('dollars');
  const resetFilters = () => { setLayout('split'); setDimension('category'); setMeasure('dollars'); };
  const isQty = measure === 'qty';
  const fmtVal = isQty ? fmtNum : fmtCur;
  const fmtAxis = isQty ? fmtNum : fmtK;
  const pane = useFilterPane();

  // Split = Item Category + Customer Category; single = the chosen dimension.
  const primaryDim = layout === 'split' ? 'item_category' : dimension;
  const q = useQuery({
    queryKey: ['analytics7v28', primaryDim, measure, pane.key],
    queryFn: () => api.getSevenVs28({ dimension: primaryDim, measure }, pane.params),
    staleTime: 60_000,
    retry: 1,
  });
  const qCust = useQuery({
    queryKey: ['analytics7v28', 'category', measure, pane.key],
    queryFn: () => api.getSevenVs28({ dimension: 'category', measure }, pane.params),
    enabled: layout === 'split',
    staleTime: 60_000,
    retry: 1,
  });
  const qRep = useQuery({
    queryKey: ['analytics7v28', 'salesman', measure, pane.key],
    queryFn: () => api.getSevenVs28({ dimension: 'salesman', measure }, pane.params),
    staleTime: 60_000,
    retry: 1,
  });

  const days = q.data?.days ?? [];
  const repVariance = (qRep.data?.rows ?? [])
    .map(r => ({ label: r.label, variance: Math.round(r.sales7 - r.prior7Avg), detail: `${fmtVal(r.sales7)} vs ${fmtVal(r.prior7Avg)}` }))
    .sort((a, b) => b.variance - a.variance);
  const dowVariance = (q.data?.dowVariance ?? [])
    .map(d => ({ label: d.dow, variance: Math.round(d.variance), detail: `${d.date}: ${fmtVal(d.sales)} vs ${fmtVal(d.prior4WkAvg)}` }));
  const anyError = q.error ?? qCust.error;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Scale size={20} /> 7 vs 28 Days
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Last 7 days compared against the weekly average of the prior 4 weeks, and the same 7 days last year.
          {q.data && (
            <span className="text-slate-500">
              {' '}· This week {q.data.last7From} → {q.data.dateTo} · vs 4-week window from {q.data.prior28From}
              {q.data.last7FromLy && <> · LY {q.data.last7FromLy} → {q.data.dateToLy}</>}
            </span>
          )}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Layout</span>
          <div className="flex items-center gap-1.5">
            {LAYOUTS.map(l => <button key={l.value} onClick={() => setLayout(l.value)} className={pill(layout === l.value)}>{l.label}</button>)}
          </div>
        </div>
        {layout === 'single' && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Dimension</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {DIMENSIONS.map(d => <button key={d.value} onClick={() => setDimension(d.value)} className={pill(dimension === d.value)}>{d.label}</button>)}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Measure</span>
          <div className="flex items-center gap-1.5">
            {MEASURES.map(m => <button key={m.value} onClick={() => setMeasure(m.value)} className={pill(measure === m.value)}>{m.label}</button>)}
          </div>
        </div>
        <div className="flex-1" />
        <AnalyticsSavedViews
          page="sales-7v28"
          getState={() => ({ layout, dimension, measure })}
          applyState={(s) => {
            if (typeof s.layout === 'string' && LAYOUTS.some(l => l.value === s.layout)) setLayout(s.layout as Layout);
            if (typeof s.dimension === 'string' && DIMENSIONS.some(d => d.value === s.dimension)) setDimension(s.dimension);
            if (typeof s.measure === 'string' && MEASURES.some(m => m.value === s.measure)) setMeasure(s.measure);
          }}
        />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={() => { if (q.data) exportRows(q.data, dimLabel(primaryDim)); if (layout === 'split' && qCust.data) exportRows(qCust.data, dimLabel('category')); }}
          disabled={!q.data}
          title="Export the comparison table(s) to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={() => { if (q.data) exportRows(q.data, dimLabel(primaryDim), exportXlsx); if (layout === 'split' && qCust.data) exportRows(qCust.data, dimLabel('category'), exportXlsx); }}
          disabled={!q.data}
          title="Export the comparison table(s) to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => { q.refetch(); qCust.refetch(); qRep.refetch(); }} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching || qCust.isFetching || qRep.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <FilterPane />

      {anyError && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the comparison</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(anyError as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !anyError && (
        <>
          {/* Tables first — the page's main job */}
          {layout === 'split' ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <CompareTable title="Item Category" data={q.data} fmtVal={fmtVal} showPayments={false} />
              <CompareTable title="Customer Category" data={qCust.data} fmtVal={fmtVal} showPayments={false} />
            </div>
          ) : (
            <CompareTable title={dimLabel(dimension)} data={q.data} fmtVal={fmtVal} showPayments />
          )}

          {/* Variance charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <VarianceChart title="Salesman variance — last 7 vs 4-week avg" data={repVariance} fmt={fmtAxis} />
            <VarianceChart title="Day-of-week variance — each day vs its 4-week avg" data={dowVariance} fmt={fmtAxis} />
          </div>

          {/* Daily strip — last 7 days, sales + payments side by side */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Last 7 days — daily sales &amp; payments</h3>
            {days.length === 0 ? (
              <div className="text-center py-6 text-slate-500 text-sm">No daily data</div>
            ) : (
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={days} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="dow" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => fmtK(Number(v))} />
                    <Tooltip
                      cursor={{ fill: '#475569', fillOpacity: 0.15 }}
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                      labelFormatter={(_, payload) => {
                        const p = payload?.[0]?.payload as { date?: string; dow?: string } | undefined;
                        return p ? `${p.dow} ${p.date}` : '';
                      }}
                      formatter={(value, name) => [fmtCur(Number(value)), name === 'sales' ? 'Sales' : 'Payments']}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }}
                      formatter={(v) => <span style={{ color: '#94a3b8' }}>{v === 'sales' ? 'Sales' : 'Payments'}</span>} />
                    <Bar dataKey="sales" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={32} />
                    <Bar dataKey="payments" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
